import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { NAV, SITE } from "@/lib/site";
import { Button } from "@/components/ui/button";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-ink/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center gap-3"
          data-testid="header-logo-link"
        >
          <img
            src={SITE.logo}
            alt="Renderwall Ltd"
            className="h-12 w-12 object-cover rounded-sm ring-1 ring-white/10"
          />
          <div className="leading-tight">
            <div className="font-serif text-xl tracking-wide text-white">
              RENDERWALL
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold/90">
              Plastering · Rendering
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <a
              key={item.id}
              href={item.href}
              data-testid={`nav-link-${item.id}`}
              className="text-sm font-medium text-white/80 hover:text-brand-gold transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="header-call-link"
            className="hidden md:inline-flex items-center gap-2 text-sm text-white/80 hover:text-brand-gold transition-colors"
          >
            <Phone className="h-4 w-4" />
            {SITE.phone}
          </a>
          <Button
            asChild
            data-testid="cta-quote-header-button"
            className="bg-brand-gold hover:bg-brand-goldDark text-black font-semibold rounded-sm tracking-wide"
          >
            <a href="#contact">Get free quote</a>
          </Button>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-sm border border-white/10 text-white"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden bg-brand-ink/95 backdrop-blur-xl border-t border-white/5"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={close}
                data-testid={`mobile-nav-link-${item.id}`}
                className="text-white/90 text-lg font-medium hover:text-brand-gold"
              >
                {item.label}
              </a>
            ))}
            <div className="h-px bg-white/10 my-2" />
            <a
              href={`tel:${SITE.phoneRaw}`}
              onClick={close}
              data-testid="mobile-call-link"
              className="inline-flex items-center gap-3 text-white"
            >
              <Phone className="h-4 w-4 text-brand-gold" /> {SITE.phone}
            </a>
            <Button
              asChild
              onClick={close}
              data-testid="cta-quote-mobile-button"
              className="bg-brand-gold hover:bg-brand-goldDark text-black font-semibold rounded-sm w-full"
            >
              <a href="#contact">Get free quote</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
